import { useState, useCallback } from 'react';

/**
 * Custom hook for managing review dialog state and actions
 * Used by RoadsideTrackingPage and RoadsideHistoryPage
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onReviewSubmitted - Callback after successful review submission
 * @returns {Object} Review dialog state and handlers
 */
export function useReviewDialog(options = {}) {
  const { onReviewSubmitted } = options;
  
  // State for the review dialog
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [hasReviewed, setHasReviewed] = useState(false);

  /**
   * Open the review dialog for a specific request
   * @param {Object} request - The request to review
   * @param {Event} e - Optional event to stop propagation
   */
  const openReviewDialog = useCallback((request, e = null) => {
    if (e) {
      e.stopPropagation(); // Prevent parent click handlers
    }
    setSelectedRequest(request);
    setShowReviewDialog(true);
  }, []);

  /**
   * Close the review dialog
   */
  const closeReviewDialog = useCallback(() => {
    setShowReviewDialog(false);
  }, []);

  /**
   * Handle successful review submission
   */
  const handleReviewSubmitted = useCallback(() => {
    setHasReviewed(true);
    if (onReviewSubmitted) {
      onReviewSubmitted();
    }
  }, [onReviewSubmitted]);

  /**
   * Update hasReviewed state based on request data
   * Call this when fetching request data
   * @param {boolean} reviewed - Whether the request has been reviewed
   */
  const updateReviewStatus = useCallback((reviewed) => {
    setHasReviewed(reviewed);
  }, []);

  /**
   * Reset the review state (useful when switching requests)
   */
  const resetReviewState = useCallback(() => {
    setShowReviewDialog(false);
    setSelectedRequest(null);
    setHasReviewed(false);
  }, []);

  return {
    // State
    showReviewDialog,
    selectedRequest,
    hasReviewed,
    
    // Actions
    openReviewDialog,
    closeReviewDialog,
    setShowReviewDialog, // For direct dialog control
    handleReviewSubmitted,
    updateReviewStatus,
    resetReviewState,
    
    // Computed - convenience method for ReviewDialog props
    reviewDialogProps: {
      open: showReviewDialog,
      onOpenChange: setShowReviewDialog,
      request: selectedRequest,
      onReviewSubmitted: handleReviewSubmitted
    }
  };
}

export default useReviewDialog;
